import { useState, useEffect, useMemo } from 'react';
import { HelpCircle, X, BookOpen, Upload, BarChart3, Brain, ShieldCheck, TrendingDown, PieChart, ChevronRight, ExternalLink, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';

interface HelpChapter {
  id: string;
  icon: React.ReactNode;
  title: string;
  badge?: string;
  description: string;
  details: string[];
}

const chapters: HelpChapter[] = [
  {
    id: 'import',
    icon: <Upload className="h-4 w-4" />,
    title: 'Portfolio Import',
    badge: 'Step 1',
    description: 'Upload your portfolio Excel file to begin the hedge analysis workflow.',
    details: [
      'Supported format: Excel (.xlsx) with columns for Ticker, Quantity, Buy Price, Current Price, Total Value.',
      'Specify your current margin usage (%) — this affects available capital for hedge strategies.',
      'The system will automatically calculate position weights and portfolio composition.',
      'Previous analyses can be reloaded from the "Previous Analyses" panel without re-importing.',
    ],
  },
  {
    id: 'simulator',
    icon: <BarChart3 className="h-4 w-4" />,
    title: 'Hedge Simulator',
    badge: 'Interactive',
    description: 'Simulate price movements and visualise how hedges protect your portfolio.',
    details: [
      'Use the price movement slider (-100% to +100%) to model market scenarios.',
      'The payoff diagram shows the combined value of your stock position plus the option hedge.',
      'Break-even markers indicate the price levels at which your hedge cost is recovered.',
      'Capital Protection Overview aggregates all positions into a single portfolio-level view.',
      'Compare different strategies side-by-side to pick the optimal protection level.',
    ],
  },
  {
    id: 'ai-analysis',
    icon: <Brain className="h-4 w-4" />,
    title: 'AI Multi-Model Analysis',
    badge: 'AI-Powered',
    description: 'Up to 5 AI models analyse your portfolio and recommend hedge strategies.',
    details: [
      '2 initial models (Gemini Flash Lite & Gemini Flash) run automatically on import.',
      '3 optional premium models (GPT-5 Mini, GPT-5, Gemini Pro) can be triggered individually.',
      'Each model provides: recommended strategy, strike selection, expiration guidance, and cost analysis.',
      'The probability-weighted engine scores strategies across 5 scenarios: Crash (1×), Bear (2×), Flat (3×), Bull (2×), Rally (1×).',
      'Results are auto-saved to your account for later review.',
    ],
  },
  {
    id: 'calculations',
    icon: <ShieldCheck className="h-4 w-4" />,
    title: 'Calculations & Greeks',
    description: 'Detailed mathematical breakdown of hedge parameters for each position.',
    details: [
      'Greeks Explained: Delta (price sensitivity), Gamma (delta acceleration), Theta (time decay), Vega (volatility sensitivity).',
      'Delta Surface heatmaps show how delta changes across strikes and expirations.',
      'Theta decay simulation lets you see how option value erodes over 30, 60, and 90-day horizons.',
      'Gamma exposure badges flag positions with Gamma > 0.05 that require closer monitoring.',
      'Fibonacci retracement levels help identify optimal strike price placement.',
    ],
  },
  {
    id: 'strategy-guide',
    icon: <BookOpen className="h-4 w-4" />,
    title: 'Strategy Guide',
    description: 'Educational reference covering common hedging strategies and when to use them.',
    details: [
      'Protective Puts: Buy puts below current price for downside insurance.',
      'Collars: Combine a protective put with a covered call to reduce net cost.',
      'Bear Put Spreads: Buy a higher-strike put and sell a lower-strike put for cheaper protection.',
      'Index Hedges: Use SPY, QQQ, IWM, or EFA options for broad portfolio protection via beta-weighted exposure.',
      'The guide includes cost/benefit comparisons and scenario-based decision trees.',
    ],
  },
  {
    id: 'sector-performance',
    icon: <TrendingDown className="h-4 w-4" />,
    title: 'Sector Performance & Short ETFs',
    description: 'Monitor sector trends and explore inverse ETF alternatives for hedging.',
    details: [
      'Sector Performance panel shows real-time sector returns and rotation analysis.',
      'Short ETF Reference lists popular inverse and leveraged ETFs (e.g., SH, SDS, SQQQ).',
      'Multi-LLM Market Outlook provides AI-generated macro views across multiple models.',
      'Use sector data to identify which parts of your portfolio face the highest risk.',
    ],
  },
  {
    id: 'diversification',
    icon: <PieChart className="h-4 w-4" />,
    title: 'Portfolio Diversification',
    badge: 'AI Scenarios',
    description: 'AI-generated diversification scenarios with structured action plans.',
    details: [
      'Multiple AI models generate independent diversification recommendations.',
      'Each scenario includes: Diversification Score (1-10), Success Probability, Risk Level, and Time Horizon.',
      'Sector Allocation tables show current vs. target weights with specific instrument suggestions.',
      '10-Step Action Plans provide prioritised, actionable steps with ticker symbols.',
      'Export scenarios to PDF for offline review or sharing with your advisor.',
      'Use the Expand All / Collapse All toggle to manage multiple model results.',
    ],
  },
];

const faqs = [
  {
    q: 'What file format does the import accept?',
    a: 'Excel (.xlsx) files with columns for Ticker, Quantity, Buy Price, Current Price, and Total Value. The system auto-detects column headers.',
  },
  {
    q: 'How many AI models can I use?',
    a: 'Up to 5 models: 2 run automatically (Gemini Flash Lite & Flash) and 3 premium models (GPT-5 Mini, GPT-5, Gemini Pro) can be triggered on demand.',
  },
  {
    q: 'Are my analyses saved automatically?',
    a: 'Yes. After a successful AI analysis, the session is auto-saved. You can also manually save at any time using the "Save Analysis" button.',
  },
  {
    q: 'What does the margin percentage affect?',
    a: 'The margin percentage tells the AI models how much buying power you have available. Higher margin usage may limit the recommended strategies to lower-cost options like spreads.',
  },
  {
    q: 'How is the probability-weighted recommendation calculated?',
    a: 'Each strategy is scored across 5 market scenarios with different weights: Flat (3×), Bear/Bull (2×), Crash/Rally (1×). The strategy with the best weighted score is recommended.',
  },
  {
    q: 'What does a high Gamma exposure mean?',
    a: 'Gamma above 0.05 means delta is changing rapidly — your hedge sensitivity is volatile near that strike. These positions need more frequent monitoring and adjustment.',
  },
  {
    q: 'Can I export the analysis results?',
    a: 'Yes. Diversification scenarios can be exported to PDF. AI analysis results are saved to your account and can be reloaded from the Previous Analyses panel.',
  },
  {
    q: 'What is an Index Hedge?',
    a: 'Instead of hedging each stock individually, you can use options on index ETFs (SPY, QQQ, IWM, EFA) to protect the whole portfolio. The system estimates the required contracts using sector-weighted beta.',
  },
  {
    q: 'How do I compare strategies side by side?',
    a: 'In the Hedge Simulator tab, the Strategy Compare Chart and Payoff Diagram let you overlay multiple strategies and see their P&L profiles across different price movements.',
  },
  {
    q: 'What if an AI model fails or times out?',
    a: 'Failed models show an error badge. You can retry by clearing and re-importing, or trigger a different optional model. Results from successful models are still available.',
  },
];

interface HedgeHelpPanelProps {
  initialChapter?: string | null;
  onChapterHandled?: () => void;
}

const HedgeHelpPanel = ({ initialChapter, onChapterHandled }: HedgeHelpPanelProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const q = search.toLowerCase().trim();

  const filteredChapters = useMemo(() => {
    if (!q) return chapters;
    return chapters.filter(ch =>
      ch.title.toLowerCase().includes(q) ||
      ch.description.toLowerCase().includes(q) ||
      ch.details.some(d => d.toLowerCase().includes(q))
    );
  }, [q]);

  const filteredFaqs = useMemo(() => {
    if (!q) return faqs;
    return faqs.filter(f =>
      f.q.toLowerCase().includes(q) ||
      f.a.toLowerCase().includes(q)
    );
  }, [q]);

  useEffect(() => {
    if (initialChapter) {
      setOpen(true);
      setSearch('');
      setTimeout(() => {
        document.getElementById(`help-${initialChapter}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
      onChapterHandled?.();
    }
  }, [initialChapter, onChapterHandled]);

  return (
    <>
      {/* Floating Help Button - always visible */}
      <Button
        onClick={() => setOpen(true)}
        size="lg"
        className="fixed bottom-6 right-6 z-50 rounded-full shadow-lg gap-2 bg-primary hover:bg-primary/90 text-primary-foreground h-14 w-14 p-0 md:w-auto md:px-5"
        aria-label="Open Help"
      >
        <HelpCircle className="h-6 w-6" />
        <span className="hidden md:inline font-semibold">Help</span>
      </Button>

      {/* Help Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
          <SheetHeader className="p-6 pb-4 border-b border-border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-xl">Hedge Protection Help</SheetTitle>
                <SheetDescription>Complete guide to all features and tools</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Search Input */}
          <div className="px-6 py-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search chapters & FAQs…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {q && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {filteredChapters.length} chapter{filteredChapters.length !== 1 ? 's' : ''} · {filteredFaqs.length} FAQ{filteredFaqs.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {/* Quick Navigation */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Feature Chapters
                </h3>
                <div className="space-y-1">
                  {filteredChapters.map((ch) => (
                    <a
                      key={ch.id}
                      href={`#help-${ch.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(`help-${ch.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <span className="text-muted-foreground group-hover:text-primary transition-colors">{ch.icon}</span>
                      <span className="text-sm font-medium text-foreground flex-1">{ch.title}</span>
                      {ch.badge && (
                        <Badge variant="secondary" className="text-xs">{ch.badge}</Badge>
                      )}
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Detailed Chapters */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Detailed Guide
                </h3>
                <Accordion type="multiple" defaultValue={q ? filteredChapters.map(c => c.id) : []} key={q} className="space-y-2">
                  {filteredChapters.map((ch) => (
                    <AccordionItem key={ch.id} value={ch.id} id={`help-${ch.id}`} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="text-primary">{ch.icon}</span>
                          <div className="text-left">
                            <p className="font-medium text-foreground">{ch.title}</p>
                            <p className="text-xs text-muted-foreground font-normal">{ch.description}</p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-2 ml-7">
                          {ch.details.map((d, i) => (
                            <li key={i} className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2">
                              <span className="text-primary mt-1 shrink-0">•</span>
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              <Separator />

              {/* FAQ Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Frequently Asked Questions
                </h3>
                <Accordion type="multiple" defaultValue={q ? filteredFaqs.map((_, i) => `faq-${i}`) : []} key={`faq-${q}`} className="space-y-2">
                  {filteredFaqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline text-left">
                        <span className="font-medium text-sm text-foreground">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* No results */}
              {q && filteredChapters.length === 0 && filteredFaqs.length === 0 && (
                <div className="text-center py-8">
                  <Search className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No results for "{search}"</p>
                </div>
              )}

              {/* Link to main Help page */}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Need more help?</p>
                    <p className="text-xs text-muted-foreground">Visit the full Help & FAQ center</p>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1" asChild>
                    <a href="/help">
                      <ExternalLink className="h-3 w-3" />
                      Help Center
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default HedgeHelpPanel;
